<script setup lang="ts">
import { BButton, BOverlay, BForm, BFormRadioGroup, BFormRadio } from 'bootstrap-vue-next';
import { ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import type { CloseReasons } from '~/api/manageIssue';
import { closeIssue } from '~/api/manageIssue';

import CloseDuplicateModal from './CloseDuplicateModal.vue';

const props = defineProps<{
  issueIid: string;
  mentions: string[];
  projectId: string;
  reasons: CloseReasons;
  webUrl: string;
  title: string;
}>();

const emits = defineEmits<{ error: [] }>();

const route = useRoute();
const router = useRouter();

const selectedReason = ref<keyof CloseReasons | null>(null);
const loading = ref(false);
const showCloseDuplicateModal = ref(false);

const handleClose = () => {
  // The radio group is required, so the browser only submits once a reason is selected.
  if (selectedReason.value === null) {
    return;
  }

  if (selectedReason.value === 'duplicate') {
    showCloseDuplicateModal.value = true;
  } else {
    void close(selectedReason.value);
  }
};

const handleCloseDuplicateConfirm = (duplicateIssueReference: string) => {
  void close('duplicate', duplicateIssueReference);
};

const handleError = () => {
  loading.value = false;
  emits('error');
};

const close = async (reason: keyof CloseReasons, duplicateIssueReference?: string): Promise<void> => {
  loading.value = true;

  const requestData = {
    issue_iid: props.issueIid,
    mentions: props.mentions,
    project_id: props.projectId,
    reason,
    web_url: props.webUrl,
    issue_title: props.title,
    duplicate_reference: duplicateIssueReference,
  };

  try {
    await closeIssue(requestData);
  } catch {
    handleError();

    return;
  }

  const { returnTo } = route.query;

  if (typeof returnTo === 'string' && returnTo !== '') {
    void router.push({ name: returnTo });
  } else {
    window.location.href = props.webUrl;
  }
};
</script>

<template>
  <BOverlay :show="loading">
    <BForm @submit.prevent="handleClose">
      <p>Select the reason the issue should be closed:</p>

      <BFormRadioGroup
        v-model="selectedReason"
        name="close-reasons"
        stacked
        required
      >
        <BFormRadio
          v-for="(value, key) in reasons"
          :key="key"
          :value="key"
        >
          <strong>{{ key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ') }}</strong>
          – {{ value.description }}
        </BFormRadio>
      </BFormRadioGroup>

      <BButton
        class="mt-3"
        variant="primary"
        type="submit"
      >
        Close issue
      </BButton>
    </BForm>

    <CloseDuplicateModal
      v-model="showCloseDuplicateModal"
      :current-issue-url="webUrl"
      @confirm="handleCloseDuplicateConfirm"
    />
  </BOverlay>
</template>
